import apiClient from "./apiClient";
import { normalizeApiError } from "../utils/apiError";

function extractData(payload) {
  return payload?.data ?? payload;
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

const reportService = {
  async getContainerWiseReport(blContainerId) {
    try {
      const params = blContainerId ? { blContainerId } : {};
      const response = await apiClient.get("/reports/container-wise", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getBLWiseReport(blId) {
    try {
      const params = blId ? { blId } : {};
      const response = await apiClient.get("/reports/bl-wise", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async downloadBLWiseReport(format, blId) {
    try {
      const normalizedFormat = String(format || "").toLowerCase();
      if (!["csv", "pdf"].includes(normalizedFormat)) {
        throw new Error("Unsupported report download format.");
      }

      const params = blId ? { blId } : {};
      const response = await apiClient.get(`/reports/bl-wise/download/${normalizedFormat}`, {
        params,
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getPurchaseMasterWiseReport(purchaseMasterId) {
    try {
      const params = purchaseMasterId ? { purchaseMasterId } : {};
      const response = await apiClient.get("/reports/purchase-master-wise", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async downloadPurchaseMasterWiseReport(format, purchaseMasterId) {
    try {
      const normalizedFormat = String(format || "").toLowerCase();
      if (!["csv", "pdf"].includes(normalizedFormat)) {
        throw new Error("Unsupported report download format.");
      }

      const params = purchaseMasterId ? { purchaseMasterId } : {};
      const response = await apiClient.get(`/reports/purchase-master-wise/download/${normalizedFormat}`, {
        params,
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getStockReport(stockCategoryId, lotId) {
    try {
      const params = {};
      if (stockCategoryId) params.stockCategoryId = stockCategoryId;
      if (lotId) params.lotId = lotId;

      const response = await apiClient.get("/reports/stock-report", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async downloadStockReport(format, stockCategoryId, lotId) {
    try {
      const normalizedFormat = String(format || "").toLowerCase();
      if (!["csv", "pdf"].includes(normalizedFormat)) {
        throw new Error("Unsupported report download format.");
      }

      const params = {};
      if (stockCategoryId) params.stockCategoryId = stockCategoryId;
      if (lotId) params.lotId = lotId;

      const response = await apiClient.get(`/reports/stock-report/download/${normalizedFormat}`, {
        params,
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getBillReport() {
    try {
      const response = await apiClient.get("/reports/bill-report");
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async downloadBillReport(format) {
    try {
      const normalizedFormat = String(format || "").toLowerCase();
      if (!["csv", "pdf", "xlsx"].includes(normalizedFormat)) {
        throw new Error("Unsupported report download format.");
      }

      const response = await apiClient.get(`/reports/bill-report/download/${normalizedFormat}`, {
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getSellingReport(params = {}) {
    try {
      const response = await apiClient.get("/reports/selling-report", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async downloadSellingReport(format, params = {}) {
    try {
      const normalizedFormat = String(format || "").toLowerCase();
      if (!["csv", "pdf", "xlsx"].includes(normalizedFormat)) {
        throw new Error("Unsupported report download format.");
      }

      const response = await apiClient.get(`/reports/selling-report/download/${normalizedFormat}`, {
        params,
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getLotWiseReport(lotId) {
    try {
      const params = lotId ? { lotId } : {};
      const response = await apiClient.get("/reports/lot-wise", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        items: toArray(dataRoot?.data || dataRoot?.items || dataRoot),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getLotWiseStockProcessing(lotId) {
    try {
      if (!lotId) throw new Error("lotId is required for stock processing report.");
      const params = { lotId };
      const response = await apiClient.get("/reports/lot-wise-stock-processing", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        data: dataRoot,
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getActiveContainers() {
    try {
      const response = await apiClient.get("/containers/active");
      const payload = response?.data || {};
      const dataRoot = extractData(payload);
      return toArray(dataRoot?.data || dataRoot?.items || dataRoot);
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getPnLReport(params = {}) {
    try {
      const response = await apiClient.get("/reports/pnl-report", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        summary: dataRoot?.summary || {},
        purchases: toArray(dataRoot?.purchases),
        expenseMaster: toArray(dataRoot?.expenseMaster),
        overheadExpenses: toArray(dataRoot?.overheadExpenses),
        labourExpenses: toArray(dataRoot?.labourExpenses),
        sales: toArray(dataRoot?.sales),
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async downloadPnLReport(format, params = {}) {
    try {
      const normalizedFormat = String(format || "").toLowerCase();
      if (!["pdf", "xlsx"].includes(normalizedFormat)) {
        throw new Error("Unsupported report download format.");
      }

      const response = await apiClient.get(`/reports/pnl-report/download/${normalizedFormat}`, {
        params,
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getAPAgingReport(params = {}) {
    try {
      const response = await apiClient.get("/reports/ap-aging", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        summary: toArray(dataRoot?.summary),
        grandTotal: dataRoot?.grandTotal || {},
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async downloadAPAgingReport(format, params = {}) {
    try {
      const normalizedFormat = String(format || "").toLowerCase();
      if (!["pdf", "xlsx", "csv"].includes(normalizedFormat)) {
        throw new Error("Unsupported report download format.");
      }

      const response = await apiClient.get(`/reports/ap-aging/download/${normalizedFormat}`, {
        params,
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getARAgingReport(params = {}) {
    try {
      const response = await apiClient.get("/reports/ar-aging", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      return {
        summary: toArray(dataRoot?.summary),
        grandTotal: dataRoot?.grandTotal || {},
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async downloadARAgingReport(format, params = {}) {
    try {
      const rawFormat = String(format || "").toLowerCase();
      const normalizedFormat = rawFormat === "excel" ? "xlsx" : rawFormat;
      if (!["pdf", "xlsx", "csv"].includes(normalizedFormat)) {
        throw new Error("Unsupported report download format.");
      }

      const response = await apiClient.get(`/reports/ar-aging/download/${normalizedFormat}`, {
        params,
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async getCustomerReport(customerId, startDate = "", endDate = "") {
    try {
      if (!customerId) throw new Error("Customer ID is required");
      const params = { customerId };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await apiClient.get("/reports/customer-report", { params });
      const payload = response?.data || {};
      const dataRoot = extractData(payload);

      const customer = dataRoot?.customerInfo || dataRoot?.customer || {};
      if (customer && !customer.name) {
        customer.name = customer.customerName;
      }
      if (customer && !customer.phone) {
        customer.phone = customer.mobileNo || customer.mobile || customer.phoneNumber || "";
      }

      const invoices = toArray(dataRoot?.invoices);
      const materials = toArray(dataRoot?.materials || invoices.flatMap(inv => inv.sellingMaterials || []));
      const payments = toArray(dataRoot?.payments || invoices.flatMap(inv => inv.payments || []));

      return {
        customer,
        invoices,
        materials,
        payments,
        raw: payload,
      };
    } catch (error) {
      throw normalizeApiError(error);
    }
  },

  async downloadCustomerReport(format, customerId, startDate = "", endDate = "") {
    try {
      const rawFormat = String(format || "").toLowerCase();
      const normalizedFormat = rawFormat === "excel" ? "xlsx" : rawFormat;
      if (!["pdf", "xlsx", "csv"].includes(normalizedFormat)) {
        throw new Error("Unsupported report download format.");
      }

      if (!customerId) throw new Error("Customer ID is required");

      const params = { customerId };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await apiClient.get(`/reports/customer-report/download/${normalizedFormat}`, {
        params,
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  },
};

export default reportService;
